import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp, NativeStackScreenProps } from '@react-navigation/native-stack';
import Icon from 'react-native-vector-icons/Feather';
import TonyteBackground from '../components/TonyteBackground';
import { auth } from '../firebase';
import { useOwnedPartnerVenues } from '../hooks/useOwnedPartnerVenues';
import { useProfile } from '../hooks/useProfile';
import { useI18n } from '../i18n';
import { Colors, Radius, Typography } from '../theme';
import type { RootStackParamList, VenueSummary } from '../types';
import { usePartnerIdentity, visiblePartnerVenues } from '../utils/partnerIdentity';
import PublicUserProfileScreen from './PublicUserProfileScreen';
import VenueProfileScreen from './VenueProfileScreen';

type OwnProps = NativeStackScreenProps<RootStackParamList, 'PartnerAccountProfile'>;
type PublicProps = NativeStackScreenProps<RootStackParamList, 'PublicProfile'>;

function PartnerDestination({ venues, onOpen }: { venues: VenueSummary[]; onOpen: (venue: VenueSummary) => void }) {
  const { t } = useI18n();
  return (
    <View style={styles.list}>
      <Text style={styles.title}>{t('partner.profileSelectorTitle', 'Choose a partner profile')}</Text>
      <Text style={styles.body}>{t('partner.profileSelectorBody', 'This account manages more than one partner.')}</Text>
      {venues.map(venue => (
        <TouchableOpacity key={venue.id} style={styles.row} onPress={() => onOpen(venue)}>
          {venue.logoUrl ? <Image source={{ uri: venue.logoUrl }} style={styles.logo} /> : (
            <View style={[styles.logo, styles.logoFallback]}><Text style={styles.logoLetter}>{venue.name.slice(0, 1).toUpperCase()}</Text></View>
          )}
          <View style={styles.copy}>
            <Text style={styles.name} numberOfLines={1}>{venue.name}</Text>
            <Text style={styles.meta} numberOfLines={1}>{[venue.category, venue.city].filter(Boolean).join(' · ')}</Text>
          </View>
          <Icon name="chevron-right" size={19} color="rgba(255,255,255,0.55)" />
        </TouchableOpacity>
      ))}
    </View>
  );
}

function MissingPartner({ failed, onRetry, onSettings }: { failed: boolean; onRetry: () => void; onSettings?: () => void }) {
  const { t } = useI18n();
  return (
    <View style={styles.center}>
      <Icon name={failed ? 'wifi-off' : 'link-2'} size={32} color={Colors.sunset} />
      <Text style={styles.title}>{t('partner.profileUnavailableTitle', 'Partner profile unavailable')}</Text>
      <Text style={styles.body}>{failed
        ? t('partner.profileLoadFailed', 'We could not check your partner access. Try again without losing your account data.')
        : t('partner.profileLinkMissing', 'Your partner account is active, but no partner profile is linked to it. Contact Tonyte support to restore access.')}</Text>
      <TouchableOpacity style={styles.primary} onPress={onRetry}><Text style={styles.primaryText}>{t('common.tryAgain')}</Text></TouchableOpacity>
      {onSettings ? <TouchableOpacity style={styles.secondary} onPress={onSettings}><Text style={styles.secondaryText}>{t('settings.title')}</Text></TouchableOpacity> : null}
    </View>
  );
}

export const PartnerAccountProfileScreen: React.FC<OwnProps> = ({ navigation }) => {
  const uid = auth.currentUser?.uid ?? null;
  const { venues, loading, error, retry } = useOwnedPartnerVenues(uid);
  useEffect(() => {
    if (!loading && venues.length === 1) navigation.replace('VenueProfile', { venueId: venues[0].id, venue: venues[0] });
  }, [loading, navigation, venues]);
  if (loading || venues.length === 1) return <TonyteBackground><View style={styles.center}><ActivityIndicator color={Colors.sunset} /></View></TonyteBackground>;
  return (
    <TonyteBackground>
      {venues.length > 1
        ? <PartnerDestination venues={venues} onOpen={venue => navigation.replace('VenueProfile', { venueId: venue.id, venue })} />
        : <MissingPartner failed={Boolean(error)} onRetry={retry} onSettings={() => navigation.navigate('Settings')} />}
    </TonyteBackground>
  );
};

// Keep the signed-in partner profile inside MainTabs so its radial navbar is
// available. Public venue profiles remain standalone stack screens.
export const PartnerAccountProfileTab: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList, 'VenueProfile'>>();
  const uid = auth.currentUser?.uid ?? null;
  const { venues, loading, error, retry } = useOwnedPartnerVenues(uid);
  const [selectedVenue, setSelectedVenue] = useState<VenueSummary | null>(null);
  const venue = selectedVenue && venues.some(item => item.id === selectedVenue.id)
    ? selectedVenue
    : venues.length === 1 ? venues[0] : null;

  if (loading) return <TonyteBackground><View style={styles.center}><ActivityIndicator color={Colors.sunset} /></View></TonyteBackground>;
  if (venue) {
    return <VenueProfileScreen
      navigation={navigation}
      route={{ key: `partner-profile-${venue.id}`, name: 'VenueProfile', params: { venueId: venue.id, venue, embeddedOwnerProfile: true } }}
    />;
  }
  return (
    <TonyteBackground>
      {venues.length > 1
        ? <PartnerDestination venues={venues} onOpen={setSelectedVenue} />
        : <MissingPartner failed={Boolean(error)} onRetry={retry} onSettings={() => navigation.navigate('Settings')} />}
    </TonyteBackground>
  );
};

// Every "open this person's profile" tap lands here. Partner accounts open
// their public partner profile (see utils/partnerIdentity); everyone else
// gets the personal profile.
export const PublicIdentityRouterScreen: React.FC<PublicProps> = props => {
  const { userId } = props.route.params;
  const { t } = useI18n();
  const viewerUid = auth.currentUser?.uid ?? null;
  const { profile: viewerProfile } = useProfile(viewerUid);
  const { identity, error, retry } = usePartnerIdentity(userId);
  const isSelf = viewerUid === userId;
  const canSeeAdminOnly = isSelf || viewerProfile?.role === 'admin' || viewerProfile?.role === 'co-admin';
  const visibleVenues = useMemo(() => visiblePartnerVenues(identity, canSeeAdminOnly), [canSeeAdminOnly, identity]);
  const isPartnerIdentity = identity?.kind === 'partner';
  useEffect(() => {
    if (isPartnerIdentity && visibleVenues.length === 1) props.navigation.replace('VenueProfile', { venueId: visibleVenues[0].id, venue: visibleVenues[0] });
  }, [isPartnerIdentity, props.navigation, visibleVenues]);
  if (identity?.kind === 'personal') return <PublicUserProfileScreen {...props} />;
  if (error) {
    return isSelf
      ? <TonyteBackground><MissingPartner failed onRetry={retry} /></TonyteBackground>
      : <TonyteBackground><PublicProfileUnavailable body={t('partner.publicProfileLoadFailed')} onRetry={retry} /></TonyteBackground>;
  }
  if (!identity || visibleVenues.length === 1) return <TonyteBackground><View style={styles.center}><ActivityIndicator color={Colors.sunset} /></View></TonyteBackground>;
  if (visibleVenues.length > 1) {
    return <TonyteBackground><PartnerDestination venues={visibleVenues} onOpen={venue => props.navigation.replace('VenueProfile', { venueId: venue.id, venue })} /></TonyteBackground>;
  }
  return isSelf
    ? <TonyteBackground><MissingPartner failed={false} onRetry={retry} /></TonyteBackground>
    : <TonyteBackground><PublicProfileUnavailable body={t('partner.publicProfileUnavailableBody')} /></TonyteBackground>;
};

// What another person sees when a partner account has no profile they may open.
function PublicProfileUnavailable({ body, onRetry }: { body: string; onRetry?: () => void }) {
  const { t } = useI18n();
  const navigation = useNavigation();
  return (
    <View style={styles.center}>
      <Icon name="user-x" size={32} color={Colors.sunset} />
      <Text style={styles.title}>{t('partner.profileUnavailableTitle')}</Text>
      <Text style={styles.body}>{body}</Text>
      {onRetry ? <TouchableOpacity style={styles.primary} onPress={onRetry}><Text style={styles.primaryText}>{t('common.tryAgain')}</Text></TouchableOpacity> : null}
      <TouchableOpacity style={styles.secondary} onPress={() => navigation.goBack()}><Text style={styles.secondaryText}>{t('common.back')}</Text></TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, paddingHorizontal: 28, alignItems: 'center', justifyContent: 'center', gap: 14 },
  list: { flex: 1, paddingTop: 80, paddingHorizontal: 20, gap: 12 },
  title: { color: '#fff', fontSize: 22, fontWeight: Typography.extrabold, textAlign: 'center' },
  body: { color: 'rgba(255,255,255,0.65)', fontSize: 14, lineHeight: 20, textAlign: 'center', marginBottom: 8 },
  row: { minHeight: 72, borderRadius: Radius.lg, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', backgroundColor: 'rgba(255,255,255,0.06)', padding: 12, flexDirection: 'row', alignItems: 'center', gap: 12 },
  logo: { width: 48, height: 48, borderRadius: 24 },
  logoFallback: { backgroundColor: Colors.purple, alignItems: 'center', justifyContent: 'center' },
  logoLetter: { color: '#fff', fontSize: 20, fontWeight: Typography.extrabold },
  copy: { flex: 1, minWidth: 0 },
  name: { color: '#fff', fontSize: 16, fontWeight: Typography.bold },
  meta: { color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 3 },
  primary: { minWidth: 180, borderRadius: Radius.pill, backgroundColor: Colors.sunset, paddingHorizontal: 20, paddingVertical: 13, alignItems: 'center' },
  primaryText: { color: '#fff', fontWeight: Typography.extrabold },
  secondary: { minWidth: 180, borderRadius: Radius.pill, borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)', paddingHorizontal: 20, paddingVertical: 13, alignItems: 'center' },
  secondaryText: { color: '#fff', fontWeight: Typography.bold },
});
